import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { useFaireDirectSignUp } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectSignUp/useFaireDirectSignUp";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useObservableWindow } from "@faire/web--source/ui/hooks/useObservableWindow";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackFaireDirectPdpSignupBannerCtaClick } from "@faire/web-api--source/events/faireDirect/click/pdpSignupBannerCta";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
export var AnimatedFaireDirectSignUpBanner = function() {
    var _useState = _sliced_to_array(useState(0), 2), bannerHeight = _useState[0], setBannerHeight = _useState[1];
    var isFaireDirectSignUpEligible = useFaireDirectSignUp().isFaireDirectSignUpEligible;
    var faireDirectSignUpBannerRef = useRef(null);
    var width = useObservableWindow().width;
    var contentNodeCallback = useCallback(function(node) {
        if (node) {
            setBannerHeight(node.offsetHeight);
        }
        faireDirectSignUpBannerRef.current = node;
    }, [
        setBannerHeight
    ]);
    useEffect(function() {
        if (faireDirectSignUpBannerRef.current) {
            setBannerHeight(faireDirectSignUpBannerRef.current.offsetHeight);
        }
    }, [
        width,
        setBannerHeight
    ]);
    if (!isFaireDirectSignUpEligible) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TransitionWrapper, {
                $bannerHeight: bannerHeight,
                children: /*#__PURE__*/ _jsx(FaireDirectSignUpBanner, {
                    ref: contentNodeCallback
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: bannerHeight
            })
        ]
    });
};
var FaireDirectSignUpBanner = function(param) {
    var ref = param.ref;
    var faireDirectBrand = useFaireDirectBrand().faireDirectBrand;
    var _faireDirectBrand_name;
    var brandName = (_faireDirectBrand_name = faireDirectBrand === null || faireDirectBrand === void 0 ? void 0 : faireDirectBrand.name) !== null && _faireDirectBrand_name !== void 0 ? _faireDirectBrand_name : "";
    var _useFaireDirectFirstOrderIncentive = useFaireDirectFirstOrderIncentive(), isFaireDirectCreditPercentageBased = _useFaireDirectFirstOrderIncentive.isFaireDirectCreditPercentageBased, formattedPercentageBasedNumber = _useFaireDirectFirstOrderIncentive.formattedPercentageBasedNumber, formattedFixedBasedAmount = _useFaireDirectFirstOrderIncentive.formattedFixedBasedAmount;
    var faireDirectSignupRequiredEmailAddressSignUpRoute = useFaireDirectSignUp().faireDirectSignupRequiredEmailAddressSignUpRoute;
    return /*#__PURE__*/ _jsxs(BannerContainer, {
        align: "center",
        ref: ref,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "displaySSerifRegular",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "3BzQPq",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sign up to shop "
                        },
                        {
                            type: 1,
                            value: "brandCompanyName"
                        }
                    ],
                    values: {
                        brandCompanyName: brandName
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "subheadingSansRegular",
                children: isFaireDirectCreditPercentageBased ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "wwGzZ1",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Get "
                        },
                        {
                            type: 1,
                            value: "percentageDiscountNumberWithUnit"
                        },
                        {
                            type: 0,
                            value: " off your first order and 1 year of free shipping."
                        }
                    ],
                    values: {
                        percentageDiscountNumberWithUnit: formattedPercentageBasedNumber
                    }
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "+ttt8c",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Get "
                        },
                        {
                            type: 1,
                            value: "discountAmountMoney"
                        },
                        {
                            type: 0,
                            value: " off your first order and 1 year of free shipping."
                        }
                    ],
                    values: {
                        discountAmountMoney: formattedFixedBasedAmount
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsx(SignUpButton, {
                linkTo: faireDirectSignupRequiredEmailAddressSignUpRoute,
                "data-test-id": "fd-sign-up-animated-banner-cta",
                onClick: trackFaireDirectPdpSignupBannerCtaClick,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "kJ127s",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sign up for free"
                        }
                    ]
                })
            })
        ]
    });
};
var TransitionWrapper = styled.div.withConfig({
    componentId: "sc-ee14021e-0"
})([
    "width:100%;position:fixed;",
    ""
], function(param) {
    var $bannerHeight = param.$bannerHeight;
    return $bannerHeight === 0 ? css([
        "z-index:-1;"
    ]) : css([
        "z-index:2;animation:slide-up 300ms ease-out forwards;@keyframes slide-up{from{opacity:0;bottom:",
        "px;}to{opacity:100%;bottom:0px;}}"
    ], -1 * $bannerHeight);
});
var BannerContainer = styled(Column).withConfig({
    componentId: "sc-ee14021e-1"
})([
    "border:1px ",
    ";background-color:",
    ";padding:",
    ";",
    "{padding:",
    ";}"
], Core.border.muted, Decorative.tint.neutral, getSpacing("6x"), responsiveMediaQueries.mobile, getSpacing("3x"));
var SignUpButton = styled(Button).withConfig({
    componentId: "sc-ee14021e-2"
})([
    "min-width:216px;",
    "{width:100%;}"
], responsiveMediaQueries.mobile);
